import { makeAutoObservable, action, computed, toJS, extendObservable, set } from 'mobx';
import WinesService from 'services/winesService';
import WineListsService from 'services/wineListsService';
import domainData from 'store/domainStore';
import tableOptions from 'pages/wineLists/config/config';
import { getInitialParams, saveParamsToStorage } from './common/filterUtils';

const STORAGE_KEY = 'wine_lists_filter_params';

const defaultData = {
  menuName: '',
  description: '',
  menuItems: [],
  customLogo: '',
  headerBackgroundColor: '',
  headerFontColor: '',
  showTableOrWineNumber: true,
  shouldResetWineRating: false,
};

// Default filter parameters
const defaultFilterParams = {
  params: {
    sort: {
      field: 'createdAt',
      order: 'desc',
    },
    filter: {},
    limit: 50,
    skip: 0,
    search: {
      value: '',
      fields: [],
    },
  },
};

const requiredFields = ['menuName', 'serviceProviderId', 'description'];

class WineLists {
  getDefaultStoreProps = () => ({
    wineLists: {
      count: 0,
      data: [],
    },
    filterData: defaultFilterParams,
    wineListState: {},
    wineListData: {},
    wineListDataErrors: {},
    selectedWines: [],

    // wines data for service provider
    spWinesData: [],
    spWinesDataLoaded: false,
    spWinesDataLoading: false,

    defaultsCountLoaded: false,
    defaultsCountLoading: false,
    defaultsCount: 0,

    spList: [],
    pageName: '',
  });

  constructor() {
    makeAutoObservable(this);
    extendObservable(this, { ...this.getDefaultStoreProps() });
  }

  @action
  reset() {
    set(this, this.getDefaultStoreProps());
  }

  @action
  setStoreWineLists(newData) {
    this.wineLists.data = [];
    newData.data.forEach(this.addStoreWineList);
    this.wineLists.count = newData.count;
  }

  @action
  addStoreWineList = element => {
    this.wineLists.data.push(element);
  };

  @action
  setFilter(filter) {
    this.filterData = filter;
    // Save to localStorage using common utility, but exclude search
    if (filter?.params) {
      const { search: _search, ...paramsWithoutSearch } = filter.params;
      const filterToSave = {
        ...filter,
        params: paramsWithoutSearch,
      };
      saveParamsToStorage(STORAGE_KEY, filterToSave);
    }
  }

  @action
  resetFilters() {
    this.setFilter(defaultFilterParams);
  }

  // Get current filter parameters using common utility with validation
  getCurrentFilterParams = (urlParams = {}) => {
    const isUserSuperAdmin = domainData.userIsSuperAdmin();
    const subscriptionAccess = domainData.getSubscriptionAllAccess();
    const userContext = { isUserSuperAdmin, subscriptionAccess };

    return getInitialParams(STORAGE_KEY, defaultFilterParams, tableOptions, userContext, urlParams);
  };

  @computed
  get filter() {
    const filterToJS = toJS(this.filterData);
    return filterToJS;
  }

  @action
  setWineListState(value) {
    this.wineListState = value;
  }

  @action
  clearSpWinesData = () => {
    this.spWinesData = [];
    this.spWinesDataLoaded = false;
    this.spWinesDataLoading = false;
    this.defaultsCount = 0;
    this.defaultsCountLoaded = false;
    this.defaultsCountLoading = false;
  };

  @action
  clearWineListDataErrors = () => {
    this.wineListDataErrors = {};
  };

  @action
  clearWineListMainData = () => {
    this.selectedWines = [];
  };

  @action
  clearWineListAllData = () => {
    this.wineListData = JSON.parse(JSON.stringify(defaultData));
    this.wineListData.serviceProviderId = '';
    this.clearWineListMainData();
  };

  @action
  initializeWineListData = currentSP => {
    this.clearWineListAllData();
    if (currentSP) {
      this.wineListData.serviceProviderId = currentSP;
    }
  };

  getStoreWineListForCreate = () => this.wineListData;

  @action
  globalSPChanged = () => {
    if (this.wineListState === 'create') {
      delete this.wineListData.menuItems;
      this.clearWineListMainData();
    }
  };

  @action
  onChangeField = e => {
    const { name, value } = e.target;
    this.wineListData[name] = value;
    if (!value && requiredFields.includes(name)) {
      this.wineListDataErrors[e.target.name] = 'required';
    } else {
      delete this.wineListDataErrors[name];
    }
    if (name === 'serviceProviderId') {
      this.globalSPChanged();
      this.clearSpWinesData();
      this.getSpWinesData(value);
      this.getSpDefaultWineListsCount(value);
    }
    return true;
  };

  @action
  updateData = data => {
    this.wineListData = {
      ...this.wineListData,
      ...data,
    };
    return true;
  };

  @action
  onChangeItemsField = (_event, selectedOptions, reason) => {
    if (reason === 'selectOption' || reason === 'removeOption') {
      if (selectedOptions.find(option => option.id === 'select-all')) {
        this.selectedWines =
          this.selectedWines.length === this.spWinesData.length
            ? []
            : this.spWinesData.map((item, index) => ({ ...item, order: index.toString() }));
      } else if (reason === 'removeOption') {
        this.selectedWines = selectedOptions;
      } else {
        this.selectedWines = selectedOptions.map((option, index) => ({ ...option, order: index.toString() }));
      }
    } else if (reason === 'clear') {
      this.selectedWines = [];
    }
    return true;
  };

  @action
  onChangeCheckBox = e => {
    this.wineListData[e.target.name] = e.target.checked;
  };

  getWineListData = isEdit => {
    const cleanData = { ...this.wineListData };
    if (isEdit) {
      delete cleanData.active;
      delete cleanData.serviceProvider;
    }
    delete cleanData.menuItems;

    return {
      ...cleanData,
      selectedWines: this.selectedWines.map(menuItem => ({
        id: menuItem.id,
        isTableOrWine: menuItem.isTableOrWine || null,
        tableOrWineNumber: menuItem.tableOrWineNumber || null,
      })),
    };
  };

  @computed
  get getSelectedWines() {
    return this.selectedWines;
  }

  setSortedSelectedWines = sortedItems => {
    this.selectedWines = sortedItems;
  };

  getMaxWineListNameSymbols = () => 255;

  setWineListData = data => {
    this.clearSpWinesData();
    this.wineListData = data;
    this.selectedWines = data.menuItems;
    if (data.serviceProviderId) {
      this.getSpWinesData(data.serviceProviderId);
      this.getSpDefaultWineListsCount(data.serviceProviderId);
    }
  };

  getWineListDataErrors = () => this.wineListDataErrors;

  wineListDataHasError = () => {
    let hasError = false;
    requiredFields.forEach(i => {
      if (!this.wineListData[i]) {
        this.wineListDataErrors[i] = 'Required';
        hasError = true;
      } else {
        this.wineListDataErrors[i] = '';
      }
    });
    return hasError;
  };

  isSpWinesDataLoaded = () => this.spWinesDataLoaded;

  @computed
  getWinesDataForSp() {
    return this.spWinesData;
  }

  loadSpWinesData = async spId => {
    this.spWinesDataLoading = true;
    try {
      const response = await WinesService.getWines({ params: { currentServiceProvider: spId } });
      if (response?.data?.data) {
        this.spWinesData = [];
        response.data.data.forEach(menuItem => {
          const order = this.selectedWines?.findIndex(item => item.id.toString() === menuItem.id.toString());
          const menuItemData = {
            id: menuItem.id.toString(),
            name: menuItem.name,
            producer: menuItem.producer,
            wineCountry: menuItem.country?.name || '',
            year: menuItem.year,
            color: menuItem.color,
            style: menuItem.style,
            sweetness: menuItem.sweetness,
            isSuperVino: menuItem.isSuperVino,
            tableOrWineNumber: null,
            isTableOrWine: null,
            order: (order + 1).toString(),
            active: menuItem.active,
            region: menuItem.region,
          };
          if (
            this.wineListData.menuItems?.length &&
            this.wineListData.menuItems.find(item => Number(item.id) === Number(menuItem.id))
          ) {
            const menuItemIndex = this.wineListData.menuItems.findIndex(
              item => Number(item.id) === Number(menuItem.id),
            );
            this.selectedWines[menuItemIndex] = {
              ...menuItemData,
              isTableOrWine: this.wineListData.menuItems[menuItemIndex].menusMenuItems.isTableOrWine,
              tableOrWineNumber: this.wineListData.menuItems[menuItemIndex].menusMenuItems.tableOrWineNumber,
            };
          }
          this.spWinesData.push(menuItemData);
        });
        this.spWinesDataLoaded = true;
        this.spWinesDataLoading = false;
      }
    } catch (err) {
      this.spWinesDataLoading = false;
    }
  };

  getSpWinesData = async spId => {
    if (this.spWinesDataLoaded) {
      return this.spWinesData;
    }
    if (this.spWinesDataLoading) {
      return new Promise(resolve => {
        const checkData = () => {
          if (this.spWinesDataLoaded) {
            resolve(this.spWinesData);
          } else {
            setTimeout(checkData, 1000);
          }
        };
        checkData();
      });
    }
    await this.loadSpWinesData(spId);
    return this.spWinesData;
  };

  loadDefaultsCount = async spId => {
    this.defaultsCountLoading = true;
    try {
      const { data } = await WineListsService.getDefaultsCount(spId);
      this.defaultsCount = data.count;
      this.defaultsCountLoaded = true;
      this.defaultsCountLoading = false;
    } catch (err) {
      this.defaultsCountLoading = false;
    }
  };

  getSpDefaultWineListsCount = async spId => {
    if (this.defaultsCountLoaded) {
      return this.defaultsCount;
    }
    if (this.defaultsCountLoading) {
      return new Promise(resolve => {
        const checkData = () => {
          if (this.defaultsCountLoaded) {
            resolve(this.defaultsCount);
          } else {
            setTimeout(checkData, 1000);
          }
        };
        checkData();
      });
    }
    await this.loadDefaultsCount(spId);
    return this.defaultsCount;
  };

  isSpDefaultWineListsCountLoaded = () => this.defaultsCountLoaded;

  getDefaultsCount = () => this.defaultsCount;

  @action
  setSpList = list => {
    this.spList = list;
  };

  @computed
  get getSpList() {
    return this.spList;
  }

  @action
  setPageName = value => {
    this.pageName = value;
  };

  @action
  handleApplyToAll = (name, value) => {
    // Create a new array to ensure MobX detects the change
    this.selectedWines = this.selectedWines.map(item => ({
      ...item,
      [name]: value,
    }));
  };

  @action
  handleChangeIsTableOrWine = (id, event) => {
    const { name, value } = event.target;

    // Find the item index first to avoid unnecessary array recreation
    const itemIndex = this.selectedWines.findIndex(item => item.id === id);
    if (itemIndex === -1) return;

    // Only update if the value actually changed
    const currentItem = this.selectedWines[itemIndex];
    if (currentItem[name] === value) return;

    // Create a new array with the updated item to ensure MobX reactivity
    const newItems = [...this.selectedWines];
    newItems[itemIndex] = { ...currentItem, [name]: value };
    this.selectedWines = newItems;
  };
}

export default new WineLists();
